import React, { useState, useEffect } from 'react';
import { uploadOverlay } from '../shared/api';
import { loadImage, getCachedImage } from '../shared/imageCache';

const SmallInput = ({ label, value, onChange, placeholder }) => (
  <div style={{ display: 'inline-block', width: '31%', marginRight: '3%', verticalAlign: 'top', marginBottom: 8 }}>
    <label style={{ display: 'block', fontSize: 10, color: '#666', marginBottom: 2 }}>{label}</label>
    <input
      type="number"
      value={value ?? ''}
      onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))}
      onKeyDown={e => {
        if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
          e.preventDefault();
          const step = parseFloat(e.target.step) || 1;
          const current = parseFloat(e.target.value) || 0;
          const delta = (e.key === 'ArrowUp' ? 1 : -1) * step * 10;
          onChange(Math.round((current + delta) * 1e6) / 1e6);
        }
      }}
      placeholder={placeholder}
      step="0.01"
      style={{
        width: '100%', padding: '4px 6px', border: '1px solid #ddd', borderRadius: 3,
        fontSize: 12, boxSizing: 'border-box'
      }}
    />
  </div>
);

const OverlayThumbnail = ({ s3Path, filename }) => {
  const [imgUrl, setImgUrl] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const s3Key = (s3Path && filename) ? s3Path + filename : null;

  useEffect(() => {
    if (!s3Key) { setImgUrl(null); setError(false); return; }

    // Check cache first
    const cached = getCachedImage(s3Key);
    if (cached) {
      // Convert the cached HTMLImageElement src to display
      setImgUrl(cached.src);
      setError(false);
      return;
    }

    setLoading(true);
    setError(false);
    loadImage(s3Key)
      .then(img => {
        setImgUrl(img.src);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [s3Key]);

  if (!s3Key) return null;

  return (
    <div style={{
      marginTop: 10, padding: 8, background: '#fafafa', border: '1px solid #eee',
      borderRadius: 4, textAlign: 'center'
    }}>
      {loading && (
        <div style={{ fontSize: 11, color: '#999', padding: 12 }}>Loading preview...</div>
      )}
      {error && (
        <div style={{ fontSize: 11, color: '#999', padding: 12 }}>Preview unavailable</div>
      )}
      {imgUrl && !error && (
        <img
          src={imgUrl}
          alt="Overlay preview"
          style={{
            maxWidth: '100%', maxHeight: 200, borderRadius: 3,
            background: 'repeating-conic-gradient(#ddd 0% 25%, #fff 0% 50%) 50% / 12px 12px'
          }}
        />
      )}
    </div>
  );
};

const LayersTab = ({ formData, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const handleOverlayUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadOverlay(file);
      onChange({
        overlay_s3_path: result.s3_path,
        overlay_filename: result.filename
      });
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.error || err.message));
    }
    setUploading(false);
  };

  return (
    <div>
      <h4 style={{ margin: '0 0 12px', fontSize: 14, color: '#333' }}>Overlay</h4>
      <p style={{ fontSize: 11, color: '#999', margin: '0 0 8px' }}>
        PNG overlay composited on top of the rendered image
      </p>

      {formData.overlay_filename ? (
        <div style={{
          padding: 12, background: '#f3e5f5', borderRadius: 4, marginBottom: 12
        }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#7b1fa2' }}>
            {formData.overlay_filename}
          </div>
          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
            {formData.overlay_s3_path}
          </div>
          <button
            onClick={() => onChange({ overlay_s3_path: null, overlay_filename: null })}
            style={{
              marginTop: 8, fontSize: 11, color: '#d32f2f',
              background: 'none', border: 'none', cursor: 'pointer'
            }}
          >
            Remove Overlay
          </button>
        </div>
      ) : (
        <div style={{
          padding: 20, border: '2px dashed #ccc', borderRadius: 4,
          textAlign: 'center', marginBottom: 12
        }}>
          <div style={{ fontSize: 12, color: '#999', marginBottom: 8 }}>
            No overlay set
          </div>
        </div>
      )}

      <label style={{
        display: 'inline-block', padding: '8px 16px',
        background: uploading ? '#ccc' : '#7b1fa2', color: '#fff',
        borderRadius: 4, cursor: uploading ? 'default' : 'pointer', fontSize: 12
      }}>
        {uploading ? 'Uploading...' : 'Upload Overlay PNG'}
        <input
          type="file"
          accept="image/png"
          onChange={handleOverlayUpload}
          disabled={uploading}
          style={{ display: 'none' }}
        />
      </label>

      <div style={{ marginTop: 16 }}>
        <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#333' }}>Or enter S3 path manually</h4>
        <input
          type="text"
          value={formData.overlay_s3_path || ''}
          onChange={e => onChange({ overlay_s3_path: e.target.value })}
          placeholder="s3://shoobphoto/overlays/"
          style={{
            width: '100%', padding: '6px 8px', border: '1px solid #ddd',
            borderRadius: 3, fontSize: 12, boxSizing: 'border-box', marginBottom: 6
          }}
        />
        <input
          type="text"
          value={formData.overlay_filename || ''}
          onChange={e => onChange({ overlay_filename: e.target.value })}
          placeholder="overlay.png"
          style={{
            width: '100%', padding: '6px 8px', border: '1px solid #ddd',
            borderRadius: 3, fontSize: 12, boxSizing: 'border-box'
          }}
        />
      </div>

      {/* Overlay thumbnail preview */}
      <OverlayThumbnail s3Path={formData.overlay_s3_path} filename={formData.overlay_filename} />

      <h4 style={{ margin: '24px 0 12px', fontSize: 14, color: '#333' }}>Mascot</h4>
      <p style={{ fontSize: 11, color: '#999', margin: '0 0 8px' }}>
        School mascot/logo placeholder. Drag to position, resize from corners. The actual logo is pulled from each school at render time.
      </p>

      {formData.mascot_center_x != null && formData.mascot_center_y != null && formData.mascot_size != null ? (
        <>
          <SmallInput
            label="Center X (%)"
            value={formData.mascot_center_x}
            onChange={v => onChange({ mascot_center_x: v })}
            placeholder="0.0-1.0"
          />
          <SmallInput
            label="Center Y (%)"
            value={formData.mascot_center_y}
            onChange={v => onChange({ mascot_center_y: v })}
            placeholder="0.0-1.0"
          />
          <SmallInput
            label="Size (%)"
            value={formData.mascot_size}
            onChange={v => onChange({ mascot_size: v })}
            placeholder="0.0-1.0"
          />
          <div style={{
            padding: 8, background: '#e8f5e9', borderRadius: 4,
            fontSize: 11, color: '#2e7d32', marginTop: 4
          }}>
            Mascot placed. Drag the green square on the canvas to reposition, or drag corners to resize.
          </div>
          <button
            onClick={() => onChange({ mascot_center_x: null, mascot_center_y: null, mascot_size: null })}
            style={{
              marginTop: 8, fontSize: 11, color: '#d32f2f',
              background: 'none', border: 'none', cursor: 'pointer'
            }}
          >
            Remove Mascot
          </button>
        </>
      ) : (
        <button
          onClick={() => onChange({ mascot_center_x: 0.5, mascot_center_y: 0.5, mascot_size: 0.15 })}
          style={{
            padding: '8px 16px', background: '#4caf50', color: '#fff',
            border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12
          }}
        >
          + Add Mascot
        </button>
      )}
    </div>
  );
};

export default LayersTab;
